import { TableFilterSubmitProps } from "@material-dev/lrw-insights-portal-ui-datatable";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "@store";
import lodash from "lodash";
import {
  CompositeMergeProjectCommand,
  CompositeMergeResponseDto,
  CompositeProjectMetadataDto,
  IProjectsDataClient,
  IDatasetPageClient,
  PageContextOfProjectDataFilter,
  ProjectDataFilter,
  ProjectsDataClient,
  DatasetPageClient,
  VariableDto,
  GetDatasetPageDictionariesQuery,
  BatteryFilterType,
  GetIncludeVariablesFilterOptionsQuery,
  GetExcludeVariablesFilterOptionsQuery,
} from "app/ApiClient";
import { VariableDtoKey } from "components/ProjectData/hooks/useProjectDataColumns";
import { SHOW_ERROR_LOAD_DELAY } from "const/config";
import { LoadingState, VariablesBatteryGroupSelectableType } from "types";

import { IState } from "..";
import ProjectsDataState, { originDatasetsState } from "./datasetsState";
import { addOrUpdateProjectSettingsExcludedVariables, removeProjectSettingsExcludedVariables, updateCurrentProjectName } from '../project/projectSlice';
import { updateProjectName } from '../projects/projectsSlice';
import { getCompositeMergeStatus, addMergeTriggerHistory, setLastMergeRequest } from '../compositeProject/compositeProjectSlice'
import { isDefaultBatteryName } from "components/ProjectData/utils";

const projectsDataClient: IProjectsDataClient = new ProjectsDataClient(process.env.REACT_APP_API_URL);
const datasetsClient: IDatasetPageClient = new DatasetPageClient(process.env.REACT_APP_API_URL);

export const getRespondentsCount = createAsyncThunk(
  "projectsData/getRespondentsCount",
  async (
    {
      modificationId,
      projectId,
      versionId,
    }: { modificationId: Nullable<number>; projectId: number; versionId: string },
    { rejectWithValue },
  ) => {
    try {
      return await projectsDataClient.getRespondentsCount(projectId, versionId, modificationId);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const rowDisabledCriteria = (row:VariablesBatteryGroupSelectableType) => !(row?.battery===undefined) && isDefaultBatteryName(row.battery);

export const datasetsSlice = createSlice({
  name: "datasets",
  initialState: originDatasetsState,
  extraReducers: (builder) => {
    builder.addCase(getRespondentsCount.pending, (state) => {
      state.respondentsCount = 0;
      state.respondentsCountLoading = true;
      state.respondentsErrorMessage = undefined;
    });
    builder.addCase(getRespondentsCount.fulfilled, (state, { payload }) => {
      state.respondentsCount = payload;
      state.respondentsCountLoading = false;

    });
    builder.addCase(getRespondentsCount.rejected, (state, { error }) => {
      state.respondentsErrorMessage = error.message;
      state.respondentsCountLoading = false;
    });
    builder.addCase(getIncludeTableFilterOptions.pending, (state) => {
      state.tableFiltersOptionsLoading = LoadingState.pending;
    });
    builder.addCase(getIncludeTableFilterOptions.fulfilled, (state, action) => {
      const payload = action.payload;

      const key = payload?.key as VariableDtoKey;
      const options = payload?.options ?? [];

      const keyExists = state.tableFiltersOptions?.some((opt) => opt.key === key);

      if (keyExists) {
        state.tableFiltersOptions = state.tableFiltersOptions?.map((opt) =>
          opt.key === key ? { ...opt, ...payload } : opt,
        );
      } else {
        state.tableFiltersOptions = [...state.tableFiltersOptions, { key, options }];
      }

      state.tableFiltersOptionsLoading = LoadingState.succeed;
    });
    builder.addCase(getIncludeTableFilterOptions.rejected, (state) => {
      state.tableFiltersOptionsLoading = LoadingState.failed;
    });
    builder.addCase(loadProjectDictionaryData.rejected, (state, { error: { message } }) => {
      state.processing = false;
      state.finished = true;
      state.succeed = false;
      state.failed = true;
      state.error = message ?? "";
    });
    builder.addCase(getCompositeProjectMetadata.pending, (state) => {
      state.compositeProjectLoadingState = LoadingState.pending;
    });

    builder.addCase(getCompositeProjectMetadata.fulfilled, (state, action: PayloadAction<CompositeProjectMetadataDto>) => {
      state.compositeProjectMetadata = action.payload;
      state.compositeProjectLoadingState = LoadingState.succeed;
    });
    builder.addCase(getCompositeProjectMetadata.rejected, (state) => {
      state.compositeProjectMetadata = null;
      state.compositeProjectLoadingState = LoadingState.failed;
    })
    builder.addCase(updateCompositeMetadata.pending, (state) => {
      state.compositeProjectLoadingState = LoadingState.pending;
    });
    builder.addCase(updateCompositeMetadata.fulfilled, (state, action: PayloadAction<CompositeProjectMetadataDto>) => {
      state.compositeProjectMetadata = action.payload;
      state.compositeProjectLoadingState = LoadingState.succeed;
    });
    builder.addCase(updateCompositeMetadata.rejected, (state) => {
      state.compositeProjectLoadingState = LoadingState.failed;
    });
    builder.addCase(compositeMergeProjects.pending, (state) => {
      state.compoisteProjectMergeStatus = LoadingState.pending;
      state.compositeProjectMergeResponse = null;
    });
    builder.addCase(compositeMergeProjects.fulfilled, (state, action: PayloadAction<CompositeMergeResponseDto>) => {
      state.compositeProjectMergeResponse = action.payload;
      const { error } = action.payload;
      if (error)
        state.compoisteProjectMergeStatus = LoadingState.failed;
      else
        state.compoisteProjectMergeStatus = LoadingState.succeed;

    });
    builder.addCase(compositeMergeProjects.rejected, (state) => {
      state.compoisteProjectMergeStatus = LoadingState.failed
    });
  },
  reducers: {
    updateIncludedTabChipText:(state, { payload }: PayloadAction<string>)=>{
      state.includedTabChipText = payload;
    },
    updateExcludedTabChipText:(state, { payload }: PayloadAction<string>)=>{
      state.excludedTabChipText = payload;
    },
    updatePreSeletedIncludedVariables: (state) => {
      state.preSelectedIncludedVariables = state.selectedIncludedVariables;
    },
    updatePreSeletedExcludedVariables: (state) => {
      state.preSelectedExcludedVariables = state.selectedExcludedVariables;
    },
    resetPreselectedvariables: (state) => {
      state.preSelectedIncludedVariables = [];
      state.preSelectedExcludedVariables = [];
    },
    resetCurrentPageData: (state) => {
      state.excludedVariables = [];
      state.includedVariables = [];
      state.selectedIncludedVariables = [];
      state.selectedExcludedVariables = [];
    },
    setExcludeDataTableFiltersQuery: (state, {payload}: PayloadAction<PageContextOfProjectDataFilter>)=>{
      state.excludePageContext = { ...payload };
    },
    updateExcludeDataTableFilters:(state, {payload}: PayloadAction<TableFilterSubmitProps<string>>)=>{
      const { columnSelector, selectedValues } = payload;
      const normalizedSelector = columnSelector as VariableDtoKey;
      const filters = state.excludePageContext.filter?.tableFilters?.filter((f) => f.key !== normalizedSelector) ?? [];
      if (selectedValues) {
        filters.push({
          key: columnSelector,
          values: selectedValues,
        });
      }

      state.excludePageContext = {
        ...state.excludePageContext,
        filter: {
          ...(state.excludePageContext.filter ?? ({} as ProjectDataFilter)),
          tableFilters: filters,
        },
      };
    },
    updateIncludeSelectedVariables:(state, { payload }: PayloadAction<{
       allSelected: boolean, 
       selectedCount: number, 
       selectedRows: VariableDto[] 
      }>) => {
        state.selectedIncludedVariables = payload.selectedRows;
        state.includedVariablesSelectedAll = payload.allSelected;
    },
    updateExcludeSelectedVariables:(state, { payload }: PayloadAction<{
       allSelected: boolean, 
       selectedCount: number, 
       selectedRows: VariableDto[] 
      }>) => {
      state.selectedExcludedVariables = payload.selectedRows;
    },
    updateIncludedVariables:(state, {payload}: PayloadAction<{datasource:VariableDto[]}>) => {
      state.includedVariables = [...payload.datasource];
      state.selectedIncludedVariables = [];
    },
    setActiveVariableTabView:(state, {payload}: PayloadAction<string>) => {
      state.activeVariableTabView = payload;
    },
    setCurrentVersionId: (state, { payload }: PayloadAction<string | null>) => {
      state.currentVersionId = payload;
      state.compositeProjectMetadata = null;
    },
    setCurrentStackId: (state, { payload }: PayloadAction<number | null>) => {
      state.currentStackId = payload;
    },
    setCurrentModificationId: (state, { payload }: PayloadAction<number | null>) => {
      state.currentModificationId = payload;
    },
    resetUpdateVariable: (state) => {
      state.currentVariable = null;
      state.updateVariableLoadingState = LoadingState.idle;
      state.updateVariableLoadingError = "";
    },
    resetProjectsData: () => originDatasetsState,
    setCurrentVariable: (state, { payload }: { payload: VariableDto }) => {
      state.currentVariable = payload;
    },
    setDictionaryData: (state, { payload }: { payload: VariableDto[] }) => {
      state.processing = false;
      state.finished = true;
      state.succeed = true;
      state.failed = false;
      state.preSelectedIncludedVariables = [];
      state.selectedIncludedVariables = [];
      state.includedVariables = payload;
    },
    setExcludeDictionaryData:(state, { payload }: { payload: VariableDto[] }) => {
      state.selectedExcludedVariables = [];
      state.preSelectedExcludedVariables = [];
      state.excludedVariables = payload;
    },
    setGenerateDictionaryDataDialogState: (state, { payload }: { payload: boolean }) => {
      state.isGenerateDictionaryDataDialogOpened = payload;
    },
    setTotalIncludedVariablesCount: (state, { payload }: { payload: number }) => {
      state.totalIncludedVariablesCount = payload;
    },
    setTotalIncludedVariablesCountWithoutFilter: (state, { payload }: { payload: number }) => {
      state.totalIncludedVariablesCountWithoutFilter = payload;
    },
    setTotalExcludedVariablesCount: (state, { payload }: { payload: number }) => {
      state.totalExcludedVariablesCount = payload;
    },
    setTotalExcludedVariablesCountWithoutFilter: (state, { payload }: { payload: number }) => {
      state.totalExcludedVariablesCountWithoutFilter = payload;
    },
    resetFilters: (state, { payload }: PayloadAction<boolean | undefined>) => {
      state.resetFilters = payload;
    },
    setPageContext: (state, { payload }: { payload: PageContextOfProjectDataFilter }) => {
      state.includePageContext = payload;
    },
    setExcludePageContext: (state, { payload }: { payload: PageContextOfProjectDataFilter }) => {
      state.excludePageContext = payload;
    },
    projectsDataStart: (state) => {
      state.processing = true;
      state.finished = false;
      state.succeed = false;
      state.failed = false;
    },
    setLoadingError: (state, { payload }: PayloadAction<boolean>) => {
      state.processing = false;
      state.showLoadingError = payload;
    },
    setHiddenColumns: (state, { payload }: PayloadAction<string[]>) => {
      state.hiddenColumns = payload;
    },
    resetCompositeProjectMatadata: (state) => {
      state.compositeProjectMetadata = null
    },
    setVariableSearchText: (state, {payload}: PayloadAction<string>) => {
      state.variableSearchString = payload;
    },
    toggleIncludedVariableAlertShow: (state) => {
      state.showIncludedAlertMessage = !state.showIncludedAlertMessage
    },
    toggleExcludedVariableAlertShow: (state) => {
      state.showExcludedAlertMessage = !state.showExcludedAlertMessage;
    },
    resetIncludedVariablesAlert: (state) => {
      state.showIncludedAlertMessage = false;
    },
    resetExcludedVariablesAlert: (state) => {
      state.showExcludedAlertMessage = false;
    },
  },
});

export const {
  projectsDataStart,
  resetCompositeProjectMatadata,
  resetProjectsData,
  resetFilters,
  resetUpdateVariable,
  setActiveVariableTabView,
  setCurrentModificationId,
  setCurrentStackId,
  setCurrentVariable,
  setCurrentVersionId,
  setDictionaryData,
  setExcludePageContext,
  setGenerateDictionaryDataDialogState,
  setHiddenColumns,
  setLoadingError,
  setPageContext,
  setTotalIncludedVariablesCount,
  setTotalIncludedVariablesCountWithoutFilter,
  setTotalExcludedVariablesCount,
  setTotalExcludedVariablesCountWithoutFilter,
  setExcludeDictionaryData,
  updateExcludeSelectedVariables,
  updateIncludedVariables,
  updateIncludeSelectedVariables,
  setVariableSearchText,
  toggleIncludedVariableAlertShow,
  toggleExcludedVariableAlertShow,
  resetIncludedVariablesAlert,
  resetExcludedVariablesAlert,
  updateExcludeDataTableFilters,
  setExcludeDataTableFiltersQuery,
  resetCurrentPageData,
  updatePreSeletedIncludedVariables,
  updatePreSeletedExcludedVariables,
  resetPreselectedvariables,
  updateIncludedTabChipText,
  updateExcludedTabChipText
} = datasetsSlice.actions;

export const projectsDataSelector = (state: RootState): ProjectsDataState => state.datasetsData;

export const clearDatasetDataDictionary = createAsyncThunk(
  "datasetsSlice/clearDatasetDataDictionary",
  async ({ projectId, versionId, stackId }: { projectId: number; versionId: string; stackId?: number }, { rejectWithValue }
  ) => {
    try{
      const result = await datasetsClient.clearDataDictionary(projectId, versionId, stackId);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loadProjectDictionaryData = createAsyncThunk(
  "datasetsSlice/loadDatasetsDictionaryData",
  async (
    {
      formDataQuery,
      stackId,
      encodedVersionId,
      modificationId,
    }: {
      formDataQuery?: PageContextOfProjectDataFilter;
      stackId?: number;
      encodedVersionId: string;
      modificationId?: number;
    },
    { dispatch, getState, signal, rejectWithValue },
  ) => {
    const state = getState() as IState;
    const { includePageContext: prevQuery, currentStackId, currentVersionId, currentModificationId, variableSearchString } = state.datasetsData;
    const { currentProject, projectSettings } = state.project;
    const { id: projectId } = currentProject ?? {};
    if (signal.aborted) {
      return;
    }
    try {
      if(!projectSettings) {
        return;
      }
      if (!projectId) {
        return;
      }
      dispatch(resetFilters(false));
      let actualQuery: PageContextOfProjectDataFilter | undefined;

      if (formDataQuery) {
        dispatch(setPageContext(formDataQuery));
        actualQuery = formDataQuery;
      } else {
        actualQuery = prevQuery;
      }
      const isDiv = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.DV);
      const isRespondables = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.Respondables);
      const request: GetDatasetPageDictionariesQuery = {
        projectId: projectId,
        encodedVersionId,
        stackId: stackId,
        context: actualQuery,
        modificationId: modificationId,
        searchPageKey: variableSearchString,
        isDV: isDiv,
        isRespondables: isRespondables,
      };

      dispatch(projectsDataStart());

      const responseData = await datasetsClient.datasetDictionary(request);
      if (signal.aborted) {
        return;
      }

      if (responseData?.pageData) {
        if (responseData?.totalCount!==undefined) {
          dispatch(setTotalIncludedVariablesCount(responseData.totalCount));
        }
        dispatch(setTotalIncludedVariablesCountWithoutFilter(responseData.totalCountWithoutFilter ?? 0));
        dispatch(setDictionaryData(responseData.pageData));
      }
      if (
        currentVersionId != encodedVersionId ||
        currentStackId != stackId ||
        currentModificationId != modificationId
      ) {
        dispatch(resetFilters(true));
      }
      dispatch(setCurrentVersionId(encodedVersionId));
      dispatch(setCurrentStackId(stackId ?? null));
      dispatch(setCurrentModificationId(modificationId ?? null));
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const loadProjectExcludeDictionaryData = createAsyncThunk(
  "datasetsSlice/loadDatasetsExcludedDictionaryData",
  async (
    {
      formDataQuery,
      stackId,
      encodedVersionId,
      modificationId,
    }: {
      formDataQuery?: PageContextOfProjectDataFilter;
      stackId?: number;
      encodedVersionId: string;
      modificationId?: number;
    },
    { dispatch, getState, signal, rejectWithValue },
  ) => {
    const state = getState() as IState;
    const { excludePageContext: prevQuery, currentStackId, currentVersionId, currentModificationId, variableSearchString } = state.datasetsData;
    const { currentProject, projectSettings } = state.project;
    const { id: projectId } = currentProject ?? {};
    if (signal.aborted) {
      return;
    }
    try {
      if (!projectId) {
        return;
      }
      if(!projectSettings) {
        return;
      }
      dispatch(resetFilters(false));
      let actualQuery: PageContextOfProjectDataFilter | undefined;

      if (formDataQuery) {
        dispatch(setExcludePageContext(formDataQuery));
        actualQuery = formDataQuery;
      } else {
        actualQuery = prevQuery;
      }

      const isDiv = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.DV);
      const isRespondables = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.Respondables);
      
      const request: GetDatasetPageDictionariesQuery = {
        projectId: projectId,
        encodedVersionId,
        stackId: stackId,
        context: actualQuery,
        modificationId: modificationId,
        searchPageKey: variableSearchString,
        isDV: isDiv,
        isRespondables: isRespondables, 
      };
      const responseData = await datasetsClient.datasetExcludedVariablesDictionary(request);
      if (signal.aborted) {
        return;
      }

      if (responseData?.pageData) {
        if (responseData?.totalCount!==undefined) {
          dispatch(setTotalExcludedVariablesCount(responseData.totalCount));
        }
        dispatch(setTotalExcludedVariablesCountWithoutFilter(responseData.totalCountWithoutFilter ?? 0));
        dispatch(setExcludeDictionaryData(responseData.pageData));
      }
      if (
        currentVersionId != encodedVersionId ||
        currentStackId != stackId ||
        currentModificationId != modificationId
      ) {
        dispatch(resetFilters(true));
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addOrUpdateExcludedVariables = createAsyncThunk(
  "datasetsSlice/addOrUpdateExcludedVariables",
  (
    {
      selectedVariables,
    }: {
      selectedVariables?: VariableDto[];
    },
    { dispatch, getState, signal, rejectWithValue },
  ) => {
    const state = getState() as IState;
    const { currentStackId, currentVersionId, currentModificationId } = state.datasetsData;
    const { currentProject, projectSettings } = state.project;
    const { id: projectId } = currentProject ?? {};
    if (signal.aborted) {
      return;
    }
    try {
      if (!projectId) {
        return;
      }
      if(!selectedVariables) {
        return;
      }
      const excludedVariableNames = lodash.compact(selectedVariables.map(x=>x.oldName));
      if(currentProject?.id && currentVersionId) {
      dispatch(addOrUpdateProjectSettingsExcludedVariables({
        projectId: currentProject.id,
        encodedVersionId: currentVersionId,
        batteries:projectSettings?.normalizedCheckedBatteries??[],
        excludedVariables:excludedVariableNames??[],
        stackId:currentStackId ?? undefined,
        batteryFilterTypes: projectSettings?.batteryFilterTypes??[],
        modificationId:currentModificationId ?? undefined,
      }));
    }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const removeExcludedVariables = createAsyncThunk(
  "datasetsSlice/removeExcludedVariables",
  (
    {
      selectedVariables,
    }: {
      selectedVariables?: VariableDto[];
    },
    { dispatch, getState, signal, rejectWithValue },
  ) => {
    const state = getState() as IState;
    const { currentStackId, currentVersionId, currentModificationId } = state.datasetsData;
    const { currentProject, projectSettings } = state.project;
    const { id: projectId } = currentProject ?? {};
    if (signal.aborted) {
      return;
    }
    try {
      if (!projectId) {
        return;
      }
      if(!selectedVariables) {
        return;
      }
      const excludedVariableNames = lodash.compact(selectedVariables.map(x=>x.oldName));
      if(currentProject?.id && currentVersionId) {
      dispatch(removeProjectSettingsExcludedVariables({
        projectId: currentProject.id,
        encodedVersionId: currentVersionId,
        batteries:projectSettings?.normalizedCheckedBatteries??[],
        excludedVariables:excludedVariableNames??[],
        stackId:currentStackId ?? undefined,
        batteryFilterTypes: projectSettings?.batteryFilterTypes??[],
        modificationId:currentModificationId ?? undefined,
      }));
    }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const checkLoadingStatus = createAsyncThunk(
  "projectsData/checkLoadingStatus",
  async ({ }, { signal, getState, dispatch }) => {
    const promise = new Promise(() => {
      setTimeout(() => {
        const { processing } = (getState() as IState).projectsData;
        if (processing) {
          if (!signal.aborted) {
            dispatch(setLoadingError(true));
          }
        }
      }, SHOW_ERROR_LOAD_DELAY);
    });
    await promise;
  },
);

export const getIncludeTableFilterOptions = createAsyncThunk(
  "projectsData/loadTableFilterOptions",
  async (
    {
      field,
      search,
      stackId,
      modificationId,
    }: { field: VariableDtoKey; search?: string; stackId?: number; modificationId?: number },
    { getState, rejectWithValue },
  ) => {
    const {
      project: { currentProject,  projectSettings},
      datasetsData: { currentVersionId }
      
    } = getState() as IState;
    try {
      const isDvSelected = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.DV)
      const isRespondablesSelected = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.Respondables)
      const query: GetIncludeVariablesFilterOptionsQuery = {
        projectId: currentProject?.id,
        encodedVersionId: currentVersionId ?? "",
        field,
        searchText: search,
        isDV:isDvSelected,
        isRespondables:isRespondablesSelected,
        stackId,
        modificationId,
      };
      const result = await datasetsClient.fetchIncludedFilterOptions(query);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getExcludeTableFilterOptions = createAsyncThunk(
  "datasets/generateExcludeTableFilterOptions",
  async (
    {
      field,
      search,
      stackId,
      modificationId,
    }: { field: VariableDtoKey; search?: string; stackId?: number; modificationId?: number },
    { getState, rejectWithValue },
  ) => {
    const {
      project: { currentProject,  projectSettings},
      datasetsData: { currentVersionId }
      
    } = getState() as IState;
    try {
      const isDvSelected = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.DV)
      const isRespondablesSelected = projectSettings?.batteryFilterTypes && projectSettings?.batteryFilterTypes.some(item=>item===BatteryFilterType.Respondables)
      const query: GetExcludeVariablesFilterOptionsQuery = {
        projectId: currentProject?.id,
        encodedVersionId: currentVersionId ?? "",
        field,
        searchText: search,
        isDV:isDvSelected,
        isRespondables:isRespondablesSelected,
        stackId,
        modificationId,
      };
      const result = await datasetsClient.fetchExcludedFilterOptions(query);
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCompositeProjectMetadata = createAsyncThunk(
  "projects/getCompositeMetadata",
  async ({ id, version }: { id: number, version: string }, { rejectWithValue }) => {
    try {
      const result = await projectsDataClient.getCompositeMetaData(id, version)
      return result;
    }
    catch (error) {
      return rejectWithValue(error);
    }
  }
)

export const compositeMergeProjects = createAsyncThunk(
  "projects/compositeMregeProjects",
  async ({ projectName, baseProjectId, appendedProjectIds }: { projectName: string, baseProjectId: string, appendedProjectIds: Array<string> },
    { rejectWithValue, dispatch }) => {
    const compositeMergeCommand: CompositeMergeProjectCommand = {
      appendedProjectIds,
      baseProjectId,
      projectName
    }
    try {
      const result = await projectsDataClient.compositeMergeProjects(compositeMergeCommand);
      dispatch(addMergeTriggerHistory(result));
      dispatch(
        setLastMergeRequest({
          commandUsed: compositeMergeCommand,
          projectId: result.originProjectId ?? "",  
        })
      );
      dispatch(getCompositeMergeStatus({ token: result.token ?? "", projectId: result.originProjectId ?? "" }));
      return result;
    }
    catch (error) {
      return rejectWithValue(error);
    }
  }
)

export const updateCompositeMetadata = createAsyncThunk(
  "projects/updateCompositeMetadata",
  async ({ id, version, projectName }: { id: number, version: string, projectName: string }, { rejectWithValue, dispatch }) => {
    try {
      const result = await projectsDataClient.updateCompositeMetadata({
        projectId: id,
        projectName,
        version
      });
      dispatch(updateCurrentProjectName({ projectId: id, projectName }));
      dispatch(updateProjectName({ projectId: id, projectName }));
      return result;
    }
    catch (error) {
      return rejectWithValue(error);
    }
  });

export const projectDataTableFilterSubmit =
  (request: TableFilterSubmitProps<string>, encodedVersionId: string, modificationId: number | undefined): AppThunk =>
    (dispatch, getState) => {
      const pageContext = getState()?.datasetsData.includePageContext;

      const { columnSelector, selectedValues } = request;
      const normalizedSelector = columnSelector as VariableDtoKey;

      const filters = pageContext.filter?.tableFilters?.filter((f) => f.key !== normalizedSelector) ?? [];

      if (selectedValues) {
        filters.push({
          key: columnSelector,
          values: selectedValues,
        });
      }

      const query = {
        ...pageContext,
        filter: {
          ...(pageContext.filter ?? ({} as ProjectDataFilter)),
          tableFilters: filters,
        },
      };
      dispatch(loadProjectDictionaryData({ formDataQuery: query, encodedVersionId, modificationId }));
    };


    export const projectExcludeDataTableFilterSubmit =
  (request: TableFilterSubmitProps<string>, encodedVersionId: string, modificationId: number | undefined): AppThunk =>
    (dispatch, getState) => {
      const pageContext = getState()?.datasetsData.excludePageContext;

      const { columnSelector, selectedValues } = request;
      const normalizedSelector = columnSelector as VariableDtoKey;

      const filters = pageContext.filter?.tableFilters?.filter((f) => f.key !== normalizedSelector) ?? [];

      if (selectedValues) {
        filters.push({
          key: columnSelector,
          values: selectedValues,
        });
      }

      const query = {
        ...pageContext,
        filter: {
          ...(pageContext.filter ?? ({} as ProjectDataFilter)),
          tableFilters: filters,
        },
      };
      dispatch(loadProjectExcludeDictionaryData({ formDataQuery: query, encodedVersionId, modificationId }));
    };
export default datasetsSlice.reducer;
